import type { EventsBookingFilters, EventsScope } from './api/types';

/**
 * Every tenant-scoped events query key starts with
 * ['events', organizationId, venueId ?? 'org']. The organizations bootstrap
 * is not tenant-scoped and lives under its own root so it can never collide
 * with, or be invalidated as, scoped data.
 */
export const eventsKeys = {
  organizations: () => ['events-organizations'] as const,
  scope: ({ organizationId, venueId }: EventsScope) =>
    ['events', organizationId, venueId ?? 'org'] as const,
  session: (scope: EventsScope) =>
    [...eventsKeys.scope(scope), 'session'] as const,
  bookings: (scope: EventsScope, filters: EventsBookingFilters = {}) =>
    [...eventsKeys.scope(scope), 'bookings', 'list', filters] as const,
  paymentSchedule: (scope: EventsScope, bookingId: number) =>
    [
      ...eventsKeys.scope(scope),
      'bookings',
      bookingId,
      'payment-schedule',
    ] as const,
  refunds: (scope: EventsScope, bookingId: number) =>
    [...eventsKeys.scope(scope), 'bookings', bookingId, 'refunds'] as const,
  leads: (scope: EventsScope) => [...eventsKeys.scope(scope), 'leads'] as const,
  pipeline: (scope: EventsScope) =>
    [...eventsKeys.scope(scope), 'pipeline'] as const,
  spaces: (scope: EventsScope, venueId: number) =>
    [...eventsKeys.scope(scope), 'venues', venueId, 'spaces'] as const,
  booking: (scope: EventsScope, bookingId: number) =>
    [...eventsKeys.scope(scope), 'bookings', bookingId, 'detail'] as const,
  timeline: (scope: EventsScope, bookingId: number) =>
    [...eventsKeys.scope(scope), 'bookings', bookingId, 'timeline'] as const,
  proposal: (scope: EventsScope, bookingId: number) =>
    [...eventsKeys.scope(scope), 'bookings', bookingId, 'proposal'] as const,
  /** Keyed by content hash: a saved edit is a new preview. */
  preview: (scope: EventsScope, versionId: number, contentHash: string) =>
    [
      ...eventsKeys.scope(scope),
      'proposal-versions',
      versionId,
      'preview',
      contentHash,
    ] as const,
  catalog: (scope: EventsScope) =>
    [...eventsKeys.scope(scope), 'catalog', 'items'] as const,
  quote: (scope: EventsScope, lines: readonly unknown[]) =>
    [...eventsKeys.scope(scope), 'pricing', 'quote', lines] as const,
  approvals: (scope: EventsScope, state: string = 'all') =>
    [...eventsKeys.scope(scope), 'approvals', state] as const,
  venueSettings: (scope: EventsScope, venueId: number) =>
    [...eventsKeys.scope(scope), 'venues', venueId, 'settings'] as const,
  rules: (scope: EventsScope) =>
    [...eventsKeys.scope(scope), 'catalog', 'rules'] as const,
};

/**
 * Matches the given resources in every venue scope of one organization.
 * A write at venue level changes what the organization-level ("All venues")
 * lists show, and the reverse, so mutations invalidate across scopes.
 */
export function inOrganization(
  organizationId: number,
  resources: readonly string[],
) {
  return ({ queryKey }: { queryKey: readonly unknown[] }) =>
    queryKey[0] === 'events' &&
    queryKey[1] === organizationId &&
    resources.includes(String(queryKey[3]));
}
